import { LegalShell, type LegalSection } from './_shared';

const SECTIONS: LegalSection[] = [
  {
    h: 'The short version',
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>
          We collect your email, the profile you create, your messages, and your location while you
          use the app.
        </li>
        <li>
          Precise location is used only in the moment, to place you at a venue and power the radar.
          We do not keep a location history.
        </li>
        <li>We do not sell your personal information and we do not run third-party ad tracking.</li>
        <li>
          You can access, correct, download, or delete your data from Settings or by emailing
          privacy@nearmirer.com.
        </li>
      </ul>
    ),
  },
  {
    h: 'Information we collect',
    body: (
      <>
        <p>
          <strong>You give us:</strong> your email address; your handle, vibe emoji, optional
          pronouns, optional bio and interest tags; the appearance blurb and section you type at a
          venue; the content of Sparks, Whispers, Circle messages and Nearmirer Pro direct messages
          (including photos you attach); reports you file.
        </p>
        <p>
          <strong>We collect automatically:</strong> approximate location from your IP address;
          precise device location (GPS / Wi-Fi / cell) while the app is open and you have granted
          permission; device and browser type; a compass heading if your device provides one;{' '}
          <strong>the IP address you sign in from and the time of each sign-in</strong>; log data
          such as timestamps and error reports; a session token stored on your device.
        </p>
        <p>We do not collect photos, contacts, government identifiers, or payment information.</p>
      </>
    ),
  },
  {
    h: 'Precise location — how we minimise it',
    body: (
      <p>
        Precise geolocation is "sensitive personal information" under laws such as the California
        Consumer Privacy Act. We only process it while you are actively using Nearmirer; we use it
        to (a) find nearby venues, (b) check you in, and (c) compute the distance and direction of
        other opted-in people. Your latest coordinates are held briefly on the venue's server
        component and overwritten on each update. Old check-ins and their coordinates are deleted
        automatically within a few hours. We never write a trail of your movements to our database,
        and we never share raw coordinates with other users — they see a distance band and a rough
        bearing only.
      </p>
    ),
  },
  {
    h: 'How we use information',
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>To operate the core features (accounts, radar, messaging, Circles).</li>
        <li>
          To keep the service safe: rate-limiting, spam and abuse detection, and acting on reports.
        </li>
        <li>To send you the login code and important service or safety notices.</li>
        <li>To comply with law and enforce our Terms.</li>
      </ul>
    ),
  },
  {
    h: 'What moderators can and cannot see',
    body: (
      <>
        <p>
          When a moderator reviews a report or opens your account, they can see: your email address;
          your current handle and any past handles; your profile (pronouns, bio, interest tags, vibe
          emoji, flavor); the dates your account was created, last active, and last signed in,{' '}
          <strong>and the IP address of your recent sign-ins</strong>; which version of the Terms
          and Privacy Policy you accepted, when,{' '}
          <strong>and the IP address at the time you accepted</strong>; your full enforcement
          history (warnings, mutes, suspensions, bans) with the moderator's stated reason for each;{' '}
          <strong>private notes our moderators add to your account</strong>; the number of reports
          filed about you and by you, with their reasons and severity;{' '}
          <strong>the number of distinct people who have blocked you</strong> — a count only, never
          who; metadata about your activity — counts and timestamps of your Sparks, Whispers and
          Circle posts, and the venue you are checked into while Glowing; any content you attach to
          a report; any appeal you submit; and, if you request Nearmirer Pro, your request and the
          note you wrote with it. For a photo you send in a message, a moderator can see that it
          exists — its file size, type and date — and can delete it to manage storage, but{' '}
          <strong>cannot view the photo itself</strong>.
        </p>
        <p>
          A moderator <strong>cannot</strong> see the text of your Whispers, Circle messages or Pro
          direct messages, except a snapshot that a user (you or someone else) chooses to attach to
          a report. They cannot open any photo you send in a message. They cannot see any history of
          your precise location — we never store one — and they cannot see your device contacts,
          photos, or identifiers. The data model keeps message bodies and image contents out of
          every moderation query path. This is a deliberate design choice.
        </p>
      </>
    ),
  },
  {
    h: 'Sharing',
    body: (
      <>
        <p>We share personal information only with:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Infrastructure providers</strong> that host and secure the service (for example,
            Cloudflare for compute, storage and network security, and an email provider to deliver
            login codes). They act on our instructions under contract.
          </li>
          <li>
            <strong>Law enforcement or others</strong> when required by valid legal process or to
            prevent imminent harm.
          </li>
          <li>
            <strong>A successor entity</strong> in a merger or acquisition, subject to this policy.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> sell personal information, and we do not "share" it for
          cross-context behavioural advertising, as those terms are defined under US state privacy
          laws. We honour the Global Privacy Control (GPC) signal.
        </p>
      </>
    ),
  },
  {
    h: 'Retention',
    body: (
      <p>
        Account and profile data: until you delete your account. Messages: until deleted by a
        participant or by our retention sweeps. Check-in coordinates: hours. Group-chat message
        copies kept for report context: up to 24 hours. Sign-in records (time and IP): a rolling
        recent window. Moderation records — enforcement history, the moderator notes on an account,
        appeals, and the audit log — are retained as long as needed for safety and legal compliance,
        in de-identified form where possible.
      </p>
    ),
  },
  {
    h: 'Your rights',
    body: (
      <>
        <p>
          Depending on where you live, you may have the right to access, correct, delete, or receive
          a portable copy of your personal information; to opt out of "sale" or "sharing" (we do
          neither) and to limit the use of sensitive information; and to appeal a decision.
          Residents of California, and of other US states with comprehensive privacy laws, have
          these rights; residents of the EU/UK/EEA have rights under the GDPR/UK GDPR.
        </p>
        <p>
          Use Settings → "Download my data" and "Delete account", or email privacy@nearmirer.com. We
          will not discriminate against you for exercising a right. We verify requests using your
          registered email.
        </p>
        <p>
          If a moderator has warned, muted, suspended, or banned your account, you can{' '}
          <strong>appeal</strong> that decision from the app (you will see the option once a
          sanction is in place) or by emailing safety@nearmirer.com. A different reviewer looks at
          each appeal.
        </p>
      </>
    ),
  },
  {
    h: 'Security',
    body: (
      <p>
        Data is encrypted in transit. Access to production systems is limited and logged. No system
        is perfectly secure; if a breach affects you we will notify you as required by law.
      </p>
    ),
  },
  {
    h: 'International transfers',
    body: (
      <p>
        Nearmirer is operated from the United States and processed on a global edge network. Where
        required, transfers of EU/UK personal data rely on Standard Contractual Clauses or another
        lawful mechanism.
      </p>
    ),
  },
  {
    h: 'Children',
    body: (
      <p>
        Nearmirer is strictly for adults 18+. We do not knowingly collect information from anyone
        under 18. If you believe a minor is using Nearmirer, contact privacy@nearmirer.com and we
        will remove the account.
      </p>
    ),
  },
  {
    h: 'Changes and contact',
    body: (
      <p>
        We will post changes here and, if material, notify you in the app. Questions or requests:
        privacy@nearmirer.com, or Nearmirer, Inc., Attn: Privacy.
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      path="/privacy"
      seoDescription="How Nearmirer handles your data and location — what we collect, why, and the choices and rights you have. Applies worldwide."
      intro={
        <p>
          This policy explains what Nearmirer collects, why, who we share it with, and the choices
          and rights you have. It applies to everyone, everywhere, and is written to meet US state
          privacy laws (including the CCPA/CPRA) and the GDPR/UK GDPR.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
